import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { buildWaLink } from "../data/site";
import { useLang } from "../data/i18n";
import { Waves } from "./Decor";
import { BrandSymbol } from "./BrandLogo";

const HERO_BG =
    "https://images.pexels.com/photos/36267908/pexels-photo-36267908.jpeg";

export default function Hero() {
    const { t } = useLang();
    return (
        <section
            id="home"
            className="relative min-h-screen w-full overflow-hidden flex items-end bg-cream"
            data-testid="hero-section"
        >
            {/* Background image with light overlay */}
            <div className="absolute inset-0">
                <img
                    src={HERO_BG}
                    alt={t.hero.bgAlt}
                    className="w-full h-full object-cover kenburns-bg"
                />
                {/* Cream tint for retro-postcard feel */}
                <div className="absolute inset-0 bg-gradient-to-b from-cream/40 via-cream/10 to-cream" />
                <div className="absolute inset-0 bg-gradient-to-r from-cream/50 via-transparent to-cream/20" />
            </div>

            {/* Decorative brand symbol — large filigree behind, low opacity */}
            <BrandSymbol
                size={560}
                className="absolute -top-24 -right-28 opacity-10 pointer-events-none hidden md:block"
            />

            {/* Side label */}
            <div className="hidden md:block absolute right-8 top-1/2 -translate-y-1/2 z-10">
                <span className="vertical-rl text-xs uppercase tracking-[0.4em] text-ocean/70 font-semibold">
                    {t.hero.side}
                </span>
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-36 pt-40 w-full">
                <motion.p
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-coral font-bold mb-8 flex items-center gap-3"
                    data-testid="hero-overline"
                >
                    <span className="inline-block w-8 h-px bg-coral" />
                    {t.hero.overline}
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="font-serif font-normal text-ocean tracking-tight leading-[0.95] text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] max-w-5xl"
                    data-testid="hero-title"
                >
                    {t.hero.titleA}
                    <br />
                    <em className="coral-italic font-normal">{t.hero.titleB}</em>
                </motion.h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.7 }}
                    className="relative mt-10 max-w-xl"
                >
                    {/* Very soft, wide cream glow — blends into the sky */}
                    <span
                        aria-hidden="true"
                        className="absolute pointer-events-none"
                        style={{
                            inset: "-90px -120px",
                            background:
                                "radial-gradient(60% 55% at 45% 50%, rgba(252,248,230,0.55) 0%, rgba(252,248,230,0.32) 35%, rgba(252,248,230,0.12) 65%, rgba(252,248,230,0) 100%)",
                            filter: "blur(28px)",
                        }}
                    />
                    <p
                        className="relative text-base md:text-lg text-ink leading-relaxed [text-shadow:0_1px_2px_rgba(252,248,230,0.65),0_0_20px_rgba(252,248,230,0.55)]"
                        data-testid="hero-subtitle"
                    >
                        {t.hero.subtitle}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.95 }}
                    className="mt-12 flex flex-wrap items-center gap-4"
                >
                    <a
                        href={buildWaLink(t.hero.waMessage)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-coral"
                        data-testid="hero-cta-primary"
                    >
                        {t.hero.ctaPrimary}
                        <span>→</span>
                    </a>
                    <a
                        href="#experiences"
                        className="inline-flex items-center gap-3 px-8 py-[16px] border-2 border-ocean text-ocean uppercase tracking-[0.18em] text-xs font-semibold hover:bg-ocean hover:text-cream transition-colors rounded-lg"
                        data-testid="hero-cta-secondary"
                    >
                        {t.hero.ctaSecondary}
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.5, delay: 1.4 }}
                    className="mt-20 flex items-center gap-3 text-ocean/70 text-xs uppercase tracking-[0.3em] font-semibold"
                >
                    <ArrowDown size={14} className="animate-bounce text-coral" />
                    {t.hero.scroll}
                </motion.div>
            </div>

            {/* Bottom wave */}
            <div className="absolute -bottom-1 left-0 right-0 pointer-events-none">
                <Waves className="w-full h-14" color="#1B6CA8" />
            </div>
        </section>
    );
}
